import React from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

import { useMediaHook } from '../../../shared/hooks/useMediaHook'
import { CommonImage } from '../../../shared/components/common-image-component'

import { useStyles } from './style'

const ImageList: React.FC<{
  list: Array<{
    src: string
    alt: string
    mobileWidth: number
    mobileHeight: number
    tabletWidth: number
    tabletHeight: number
  }>
  spacing?: boolean
}> = ({ list, spacing }) => {
  const classes = useStyles()
  const { isMobile } = useMediaHook()

  const imageWrapperClasses = [classes.imagesContainer]
  if (spacing) {
    imageWrapperClasses.push(classes.imagesContainerSpacing)
  }

  return (
    <Box component={'section'} display='flex' className={imageWrapperClasses.join(' ')}>
      {list.map((data, index) => {
        return (
          <Box key={index} className={classes.imagesWrapper}>
            <CommonImage
              alt={data.alt}
              src={data.src}
              width={isMobile ? data.mobileWidth : data.tabletWidth}
              height={isMobile ? data.mobileHeight : data.tabletHeight}
            />
          </Box>
        )
      })}
    </Box>
  )
}

export const PartnersSection: React.FC = () => {
  const classes = useStyles()

  return (
    <>
      <Box component={'section'} className={classes.partnerSection}>
        <Typography variant={'h2'} component={'h2'}>
          Funding partners
        </Typography>
        <Typography variant={'body1'} component={'p'}>
          Phase 1: April 2018 to March 2023
        </Typography>
        <Typography variant={'body1'} component={'p'}>
          Funded by: AgriScience Program as part of the Canadian Agricultural Partnership, a federal, provincial,
          territorial initiative.
        </Typography>
        <ImageList list={partnersData} />
      </Box>
      <Box component={'section'} className={classes.partnerSection}>
        <Typography variant={'body1'} component={'p'}>
          Phase 2: April 2023 to March 2026
        </Typography>

        <Typography variant={'body1'} component={'p'}>
          Funded by: the Sustainable Canadian Agricultural Partnership (Canada-Manitoba Bilateral Agreement)
        </Typography>
        <ImageList list={scapFunders} />

        <Typography variant={'body1'} component={'p'}>
          With in-kind support from:
        </Typography>

        <ImageList list={scapSupporters} spacing />
      </Box>

      <Box component={'section'} className={classes.partnerSection}>
        <Typography variant={'body1'} component={'p'}>
          Phase 3: April 2026 to March 2028
        </Typography>
        <Typography variant={'body1'} component={'p'}>
          Funded by: Prairie Provincial Wheat and Barley Producer Group Consortium
        </Typography>
        <ImageList list={ppwBPGC} spacing />
        <Typography variant={'body1'} component={'p'}>
          With in-kind support from:
        </Typography>
        <ImageList list={ppwBPGCAdditional} spacing />
      </Box>
    </>
  )
}

const partnersData = [
  {
    src: '/images/partners/canArgiPartn.jpg',
    alt: 'Canadian Agricultural Partnership logo',
    mobileWidth: 106,
    mobileHeight: 51,
    tabletWidth: 170,
    tabletHeight: 82
  },
  {
    src: '/images/partners/argiAndAgriFoodCan.jpg',
    alt: 'Agriculture and Agri-Food Canada logo',
    mobileWidth: 156,
    mobileHeight: 48,
    tabletWidth: 225,
    tabletHeight: 69
  },
  {
    src: '/images/partners/integrCropAgrCluster.jpg',
    alt: 'Integrated Crop Agronomy Cluster logo',
    mobileWidth: 143,
    mobileHeight: 56,
    tabletWidth: 223,
    tabletHeight: 87
  },
  {
    src: '/images/partners/brewMaltBarlResInst.jpg',
    alt: 'Brewing and Malting Barley Research Institute logo',
    mobileWidth: 134,
    mobileHeight: 74,
    tabletWidth: 211,
    tabletHeight: 118
  },
  {
    src: '/images/partners/saskWheat.jpg',
    alt: 'Sask Wheat logo',
    mobileWidth: 150,
    mobileHeight: 56,
    tabletWidth: 229,
    tabletHeight: 91
  },
  {
    src: '/images/partners/wgrf.jpg',
    alt: 'WGRF logo',
    mobileWidth: 139,
    mobileHeight: 54,
    tabletWidth: 230,
    tabletHeight: 88
  },
  {
    src: '/images/partners/mca.jpg',
    alt: 'Manitoba Crop Alliance logo',
    mobileWidth: 130,
    mobileHeight: 55,
    tabletWidth: 229,
    tabletHeight: 98
  },
  {
    src: '/images/partners/albertaWheat.jpg',
    alt: 'Alberta Wheat Commission logo',
    mobileWidth: 138,
    mobileHeight: 54,
    tabletWidth: 193,
    tabletHeight: 93
  },
  {
    src: '/images/partners/pog.jpg',
    alt: 'Prairie Oat Growers Association logo',
    mobileWidth: 143,
    mobileHeight: 69,
    tabletWidth: 178,
    tabletHeight: 97
  }
]

const scapFunders = [
  {
    src: '/images/partners/sustainable-canadian-agricultural-partnership.png',
    alt: 'Sustainable Canadian Agricultural Partnership logo',
    mobileWidth: 142,
    mobileHeight: 60,
    tabletWidth: 250,
    tabletHeight: 104
  },
  {
    src: '/images/partners/mca.jpg',
    alt: 'Manitoba Crop Alliance logo',
    mobileWidth: 130,
    mobileHeight: 55,
    tabletWidth: 229,
    tabletHeight: 98
  },
  {
    src: '/images/partners/wgrf.jpg',
    alt: 'Western Grains Research Foundation logo',
    mobileWidth: 139,
    mobileHeight: 54,
    tabletWidth: 230,
    tabletHeight: 88
  },
  {
    src: '/images/partners/saskWheat.jpg',
    alt: 'Sask Wheat logo',
    mobileWidth: 150,
    mobileHeight: 56,
    tabletWidth: 229,
    tabletHeight: 91
  }
]

const scapSupporters = [
  {
    src: '/images/partners/alberta-grains.png',
    alt: 'Alberta Grains logo',
    mobileWidth: 160,
    mobileHeight: 83,
    tabletWidth: 196,
    tabletHeight: 102
  },
  {
    src: '/images/partners/sask-barley.png',
    alt: 'Sask Barley logo',
    mobileWidth: 135,
    mobileHeight: 32,
    tabletWidth: 203,
    tabletHeight: 48
  },
  {
    src: '/images/partners/alberta.png',
    alt: 'Alberta logo',
    mobileWidth: 150,
    mobileHeight: 42,
    tabletWidth: 225,
    tabletHeight: 63
  },
  {
    src: '/images/partners/saskatchewan.png',
    alt: 'Saskatchewan logo',
    mobileWidth: 134,
    mobileHeight: 28,
    tabletWidth: 202,
    tabletHeight: 42
  },
  {
    src: '/images/partners/manitoba.png',
    alt: 'Manitoba logo',
    mobileWidth: 121,
    mobileHeight: 26,
    tabletWidth: 193,
    tabletHeight: 41
  }
]

const ppwBPGC = [
  {
    src: '/images/partners/alberta-grains.png',
    alt: 'Alberta Grains logo',
    mobileWidth: 160,
    mobileHeight: 83,
    tabletWidth: 196,
    tabletHeight: 102
  },
  {
    src: '/images/partners/sask-barley.png',
    alt: 'Sask Barley logo',
    mobileWidth: 135,
    mobileHeight: 32,
    tabletWidth: 203,
    tabletHeight: 48
  },
  {
    src: '/images/partners/saskWheat.jpg',
    alt: 'Sask Wheat logo',
    mobileWidth: 150,
    mobileHeight: 56,
    tabletWidth: 229,
    tabletHeight: 91
  },
  {
    src: '/images/partners/mca.jpg',
    alt: 'Manitoba Crop Alliance logo',
    mobileWidth: 130,
    mobileHeight: 55,
    tabletWidth: 229,
    tabletHeight: 98
  }
]

const ppwBPGCAdditional = [
  {
    src: '/images/partners/alberta.png',
    alt: 'Alberta logo',
    mobileWidth: 150,
    mobileHeight: 42,
    tabletWidth: 225,
    tabletHeight: 63
  },
  {
    src: '/images/partners/saskatchewan.png',
    alt: 'Saskatchewan logo',
    mobileWidth: 134,
    mobileHeight: 28,
    tabletWidth: 202,
    tabletHeight: 42
  },
  {
    src: '/images/partners/manitoba.png',
    alt: 'Manitoba logo',
    mobileWidth: 121,
    mobileHeight: 26,
    tabletWidth: 193,
    tabletHeight: 41
  }
]
